import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import classnames from 'classnames';
import { isArray, isEmpty, isNull } from 'lodash';
import React, {
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import {
  ProjectContext,
  ProjectContextValue,
} from '../../../contexts/ProjectContext';
import RolesContext from '../../../contexts/RolesContext';
import InspectionListing from '../../Inspections/Listing/InspectionListing';
import SelectAll from '../../Inspections/SelectAll';
import Loading from '../../Shared/ResourceIndex/Loading';
import SuccessNotification from '../../Shared/SuccessNotification/SuccessNotification';
import InspectionDate from '../InspectionDate';
import BulkCertifyModal from './BulkCertifyModal';
import AppContext from '../../../contexts/AppContext';
import useResource from '../../../hooks/resource';
import ExternalProjectsInspectionListing from '../../Inspections/Listing/ExternalProjectsInspectionListing';
import UpcomingInspectionSection from './UpcomingInspectionSection';
import { Button, Modal } from '@stormwater-software/lib-ui';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import { getSchedularFields } from '../Forms/projectFormSchema';
import divisionApi from '../../../utils/api/division';
import { AppDivisionContext } from '../../../contexts/AppDivisionContext';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import inspectionTemplateTypesApi from '../../../utils/api/inspectionTemplateTypes';
import AssignedFormInspectionModal from './AssignedFormInspectionModal';
import projectApi from '../../../utils/api/project';
import {
  useInspectionTypesByTemplate,
  defaultInspectionTypeOptions,
} from '../../../hooks/inspectionTypes';
import projectInspectionApi from '../../../utils/api/projectInspection';
import { toast } from 'react-toastify';
import authorizationAgreementApi, {
  AgreementState,
} from '../../../utils/api/authorizationAgreement';
import AuthorizationAgreementModal from './AuthorizationAgreementModal';
import AuthorizationAgreementBanner from './AuthorizationAgreementBanner';

const controlField = 'inspectionFilter';

export const findingsNotRequiredForCompliance = (
  template: any,
  findings: any,
  findingDetails: any,
) => {
  let isNotRequired = true;

  findings.forEach((ele: any) => {
    if (!ele.date_completed) {
      template &&
        findingDetails &&
        findingDetails.length &&
        findingDetails.forEach((detail: any) => {
          if (
            template.id === detail.inspection_template_id &&
            ele.finding_type_id === detail.finding_type_id
          ) {
            if (detail.is_required_for_cert) {
              isNotRequired = false;
            }
          }
        });
    }
  });

  return isNotRequired;
};

const ResetButton = (props: { handleClick: Dispatch<void> }) => {
  const formContext = useContext(FormContext);

  return (
    <button
      className='primary'
      onClick={() => {
        formContext.set(controlField, 'all');
        props.handleClick();
      }}
    >
      Reset filter
    </button>
  );
};

const filterInspections = (
  input: ProjectContextValue['inspections'],
  filter: string,
) => {
  return input.filter((inspection) => {
    if (filter === 'all') {
      return true;
    }

    return inspection.inspection_types
      ?.map((inspectionType: any) => inspectionType.type)
      .includes(filter);
  });
};

/**
 * SW2-2334 — "don't show this again" for the One Time explainer.
 *
 * Keyed per user id: these machines are shared between inspectors, and one person
 * dismissing the explanation should not silently suppress it for the next.
 *
 * Every access is guarded. localStorage throws outright in some privacy modes rather
 * than returning null, and a dismissed hint is not worth breaking the Schedule
 * Inspection button over — on failure we simply show the explainer.
 */
const ONE_TIME_EXPLAINER_KEY = 'sw2:oneTimeExplainerDismissed';

const hasDismissedOneTimeExplainer = (userId?: number | string): boolean => {
  if (!userId) return false;

  try {
    return localStorage.getItem(`${ONE_TIME_EXPLAINER_KEY}:${userId}`) === 'true';
  } catch {
    return false;
  }
};

const dismissOneTimeExplainer = (userId?: number | string): void => {
  if (!userId) return;

  try {
    localStorage.setItem(`${ONE_TIME_EXPLAINER_KEY}:${userId}`, 'true');
  } catch {
    /* a hint we cannot remember is not worth failing the flow for */
  }
};

const Inspections = () => {
  const history = useHistory();
  const location = useLocation<any>();
  const match = useRouteMatch<{ id: string }>([
    '/divisions/:division_id/projects/archive/:id/inspections',
    '/divisions/:divisions_id/projects/:id/inspections',
    '/public/divisions/:divisions_id/projects/:id/inspections',
  ]);

  if (!match || !match.params.id) {
    throw new Error('URL parameter `id` is required');
  }

  const permCheck = useContext(RolesContext).userHasPermission;
  const role = useContext(AppContext).get('user').roleName;
  const currentUserId = useContext(AppContext).get('user')?.id;
  const projectStore = useContext(ProjectContext);
  const inspections = projectStore.inspections;
  const startInspectionButtonRef = useRef<any>();
  const [busyCreating, setBusyCreating] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [filteredInspections, setFilteredInspections] = useState<
    typeof inspections
  >(inspections || []);
  const [hasSelectedAllInspections, setHasSelectedAllInspections] =
    useState(false);
  const [selectedInspections, setSelectedInspections] = useState<Array<number>>(
    [],
  );
  const [showCertifyModal, setShowCertifyModal] = useState(false);
  const [schedularModal, setSchedularModal] = useState(false);
  // SW2-2334 — sits between the assigned-form modal and the One Time flow.
  const [oneTimeExplainer, setOneTimeExplainer] = useState(false);
  const [suppressOneTimeExplainer, setSuppressOneTimeExplainer] =
    useState(false);
  const [assignedFormModal, setAssignedFormModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [contacts, setContacts] = useState<any[]>([]);
  const [inspectionTemplates, setInspectionTemplates]: any = useState([]);
  const [inspectionTemplateTypes, setInspectionTemplateTypes]: any = useState(
    [],
  );
  const [inspectionTypeOptions, setInspectionTypeOptions] = useState<string[]>(
    defaultInspectionTypeOptions,
  );
  const [scheduleFormData, setScheduleFormData]: any = useState({});
  const [filterFormData, setFilterFormData]: any = useState({});
  const { appDivisionId } = useContext(AppDivisionContext);
  const [upcomingRefreshToken, setUpcomingRefreshToken] = useState(0);
  const [showSuccess, setSuccessVis] = useState(false);
  const [showSelectAllBtn, setShowSelectAllBtn] = useState(false);
  const [pageVal, setPageVal] = useState();
  const [activeFilters, setActiveFilters] = useState<{
    inspectionTemplateTypeId?: number;
    inspectionType?: string;
  }>({});
  const [agreementState, setAgreementState] = useState<AgreementState | null>(
    null,
  );
  const [showAgreementModal, setShowAgreementModal] = useState(false);

  const { data: inspectionTypesData, isLoading: isLoadingInspectionTypes } =
    useInspectionTypesByTemplate(
      scheduleFormData.schedule_template
        ? +scheduleFormData.schedule_template
        : undefined,
      schedularModal && !!scheduleFormData.schedule_template,
    );

  const { pagination, data, query } = useResource({
    resource: 'projectInspections',
    disablePagination: false,
    projectId: projectStore.project ? projectStore.project.id : undefined,
    pageSize: pageVal,
    inspectionTemplateTypeId: activeFilters.inspectionTemplateTypeId,
    inspectionType: activeFilters.inspectionType,
  });

  const noInspections = useMemo(() => {
    return (
      projectStore.inspectionsQuery.isFetched &&
      isEmpty(projectStore.inspectionsQuery.data || []) &&
      isNull(projectStore.nextInspectionDate)
    );
  }, [
    projectStore.inspectionsQuery.data,
    projectStore.inspectionsQuery.isFetched,
    projectStore.nextInspectionDate,
  ]);

  /**
   * Inspections that are awaiting compliance signature
   *
   * Must be signed by inspector
   */
  const inspectionsReadyForCompliance = filteredInspections.filter(
    (inspection) =>
      inspection.compliance_date === null &&
      inspection.certification_date !== null &&
      inspection.status === 'complete' &&
      // SW2-2248 — a pending review holds distribution; bulk compliance-certify must not sweep
      // it in either, since the backend's own bulk endpoints skip it (`bulkCertify`'s
      // `skippedForReview`) and would silently no-op the selection otherwise.
      !inspection.pending_review &&
      findingsNotRequiredForCompliance(
        inspection.inspection_template,
        inspection.findings,
        inspection.findingDetails,
      ),
  );

  const handleSaveAnimation = useCallback(() => {
    setSelectedInspections([]);
    window.setTimeout(() => setSuccessVis(false), 2000);

    return setSuccessVis(true);
  }, [showSuccess]);

  const selectInspection = (id: number) => {
    // manage selected list
    if (selectedInspections.includes(id)) {
      setSelectedInspections(
        [...selectedInspections].filter((inspectionId) => inspectionId !== id),
      );
      setHasSelectedAllInspections(false);
    } else {
      setSelectedInspections([...selectedInspections, id]);
    }
  };

  /** on load */
  useEffect(() => {
    if (location.state && location.state.checkAll && projectStore.inspections) {
      setSelectedInspections(
        projectStore.inspections.map(
          (inspection: { id: number }) => inspection.id,
        ),
      );
    }
  }, [projectStore.inspections]);

  /** watch data source and filter to set filtered inspections */
  useEffect(() => {
    if (projectStore.inspections) {
      setFilteredInspections(
        filterInspections(projectStore.inspections, filter),
      );

      if (
        projectStore.inspections.length > 0 &&
        projectStore.inspections[0].inspection_template &&
        projectStore.inspections[0].inspection_template.has_compliance_cert ===
          true &&
        navigator.onLine
      ) {
        setShowSelectAllBtn(true);
      } else {
        setShowSelectAllBtn(false);
      }
    }
  }, [projectStore.inspections, filter]);

  useEffect(() => {
    if (hasSelectedAllInspections) {
      setSelectedInspections(
        inspectionsReadyForCompliance.reduce<number[]>((list, inspection) => {
          if (inspection.id) {
            list.push(inspection.id);
          }

          return list;
        }, []),
      );

      return;
    }

    if (inspectionsReadyForCompliance.length >= selectedInspections.length) {
      setSelectedInspections([]);
    }
  }, [hasSelectedAllInspections]);

  useEffect(() => {
    projectStore.inspectionsQuery.refetch();

    // Fetch contacts and inspection templates for the schedule modal
    const fetchScheduleData = async () => {
      try {
        const projectId = projectStore?.project?.id;

        if (!projectId) {
          return;
        }

        const [contactsRes, templatesRes] = await Promise.all([
          projectApi.contacts.index(projectId),
          divisionApi.inspectionTemplates.index(appDivisionId || 1),
        ]);

        setContacts(
          contactsRes.filter((contact: any) =>
            contact.userTenantRoles?.[0]?.status === 'INACTIVE' ||
            contact.status === 'INACTIVE'
              ? false
              : true,
          ),
        );
        setInspectionTemplates(templatesRes);
      } catch (error) {
        console.error('Error fetching schedule data:', error);
      }
    };

    if (appDivisionId && projectStore?.project?.id) {
      fetchScheduleData();
    }
  }, [appDivisionId, projectStore?.project?.id]);

  const fetchAgreementState = React.useCallback((projectId: number) => {
    authorizationAgreementApi
      .getAgreement(projectId)
      .then((state) => {
        setAgreementState(state);
        if (
          state.required &&
          (!state.agreement || state.agreement.status !== 'executed')
        ) {
          setShowAgreementModal(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const projectId = projectStore?.project?.id;

    if (!projectId) return;
    fetchAgreementState(projectId);
  }, [projectStore?.project?.id]);

  const handleDownloadAgreement = async () => {
    const projectId = projectStore?.project?.id;

    if (!projectId) return;
    try {
      const blob = await authorizationAgreementApi.downloadPdf(projectId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `authorization-agreement-project-${projectId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent — pdf not yet available
    }
  };

  let buttonDisabledReason = '';

  const canCreateNewInspections =
    projectStore.template && projectStore.template.name === 'CDOT'
      ? true
      : true;

  if (!canCreateNewInspections) {
    buttonDisabledReason =
      'Complete the existing inspections(s) before creating a new one.';
  }

  const inspectionDueToday = projectStore.inspectionDueToday();
  const isDownloaded = projectStore.isDownloaded();
  const restrictedRoles = [
    'Regulator',
    'Regulator - With Photos',
    'Certifying Regulator',
    'COA Regulator',
    'Inspector',
    'Supervising Engineer',
  ];
  const isPublicUser = location.pathname.startsWith('/public');

  return (
    <section
      className={classnames('project-detail-inspections tab-pane-content', {
        'project-detail-inspections-cta-routine': noInspections,
      })}
    >
      {query.isError ? (
        <div className='inspection-listing-error'>
          <p>Failed to load inspections. Please try again.</p>
          <button className='btn primary' onClick={() => query.refetch()}>
            Retry
          </button>
        </div>
      ) : isArray(projectStore.inspectionsQuery.data) ? (
        <React.Fragment>
          {!isPublicUser &&
            !restrictedRoles.includes(role) &&
            (agreementState?.required ||
              agreementState?.agreement?.status === 'executed' ||
              agreementState?.agreement?.waived_at) && (
              <AuthorizationAgreementBanner
                projectId={projectStore.project!.id}
                agreement={agreementState?.agreement ?? null}
                signability={agreementState!.signability}
                onSignNow={() => setShowAgreementModal(true)}
                onDownload={handleDownloadAgreement}
                onAgreementUpdated={() =>
                  fetchAgreementState(projectStore.project!.id)
                }
              />
            )}
          {/** @todo move this closer to the form where it's needed...where is it needed? */}
          <FormContextProvider>
            <div
              className={classnames('tab-pane-actions', {
                'begin-inspection-schedule-actions': noInspections,
              })}
            >
              <div className='tab-pane-actions-secondary'>
                {inspectionDueToday &&
                  inspectionDueToday.status !== 'complete' &&
                  !isDownloaded &&
                  projectStore.project?.status === 'active' &&
                  permCheck('update', 'Inspection') && (
                    <div className='action-due-today-cta'>
                      <span>You have an inspection due today</span>

                      <ReactTooltip />

                      <button
                        className='small'
                        ref={startInspectionButtonRef.current}
                        onMouseOver={() => {
                          if (!canCreateNewInspections) {
                            ReactTooltip.show(startInspectionButtonRef.current);
                          }
                        }}
                        data-tip={
                          agreementState?.required &&
                          agreementState?.agreement?.status !== 'executed'
                            ? 'An Authorization & Agreement must be signed before starting this inspection.'
                            : buttonDisabledReason
                        }
                        onClick={
                          // canCreateNewInspections
                          //   ?
                          () => {
                            setBusyCreating(true);

                            return projectStore
                              .startRoutineInspection('project')
                              .then((inspectionId) => {
                                /** @todo rework into switch with Redirect component  */
                                history.push(
                                  `/inspection/${inspectionId}/questionnaire`,
                                );
                              });
                          }
                          // :
                          // () => { }
                        }
                        disabled={
                          !canCreateNewInspections ||
                          busyCreating ||
                          !!(
                            agreementState?.required &&
                            agreementState?.agreement?.status !== 'executed'
                          )
                        }
                        style={
                          agreementState?.required &&
                          agreementState?.agreement?.status !== 'executed'
                            ? { cursor: 'not-allowed', pointerEvents: 'auto' }
                            : undefined
                        }
                      >
                        {inspectionDueToday.status === 'new'
                          ? 'Start Inspection'
                          : 'Complete Inspection'}
                      </button>
                    </div>
                  )}

                {permCheck('update', 'Project') && (
                  <InspectionDate className='action-edit-date' />
                )}
              </div>

              {/* Inspection Type Selection Modal */}
              {/*
                SW2-2334 — the Assigned Form / One Time chooser is retired for now.
                "Schedule Inspection" opens the assigned-form modal directly, since that
                is the flow that can carry a date onto the form itself. The One Time modal
                below is left in place but unreachable rather than deleted, so restoring
                the choice is a one-line change if it is wanted back.
              */}

              {/* Assigned Form Inspection Modal */}
              {assignedFormModal && (
                <FormContextProvider>
                  <AssignedFormInspectionModal
                    show={assignedFormModal}
                    onClose={() => setAssignedFormModal(false)}
                    onSubmit={() => {
                      setAssignedFormModal(false);
                      setUpcomingRefreshToken((prev) => prev + 1);
                      projectStore.inspectionsQuery.refetch();
                    }}
                    onUseOneTime={() => {
                      setAssignedFormModal(false);

                      // Already read and dismissed: go straight through rather than
                      // re-explaining something they told us they understand.
                      if (hasDismissedOneTimeExplainer(currentUserId)) {
                        setSchedularModal(true);

                        return;
                      }

                      setOneTimeExplainer(true);
                    }}
                    projectId={projectStore?.project?.id}
                  />
                </FormContextProvider>
              )}

              {/*
                SW2-2334 — an interstitial rather than opening One Time straight away.
                Measured over 90 days in production: 1,657 one-time inspections, of which
                889 (54%) used a form ALREADY assigned to that project, so the assigned-form
                modal would have done the job. Nobody used it to schedule ahead
                (pending_future = 0). The remaining 768 were forms genuinely not assigned —
                which is what this flow is actually for, and why it is not simply removed.
              */}
              <Modal
                show={oneTimeExplainer}
                title='One Time Inspection'
                handleClose={() => setOneTimeExplainer(false)}
                handleSubmit={() => {
                  if (suppressOneTimeExplainer) {
                    dismissOneTimeExplainer(currentUserId);
                  }
                  setOneTimeExplainer(false);
                  setSchedularModal(true);
                }}
                submitBtnText='Continue'
                cancelBtn={true}
              >
                <div className='one-time-explainer'>
                  <p>
                    <strong>
                      Use this only when the form you need is not assigned to this
                      project.
                    </strong>
                  </p>
                  <p>
                    If the form is already assigned, go back and use{' '}
                    <em>Schedule Inspection</em> instead — it keeps the inspection tied
                    to the project&apos;s form, so its due dates, interval and inspector
                    stay correct.
                  </p>
                  <p>
                    A one-time inspection is a standalone record. It does not update the
                    form&apos;s schedule, it will not appear against that form&apos;s
                    recurring inspections, and it does not inherit the form&apos;s
                    configuration.
                  </p>
                  <p>
                    If you find yourself doing this repeatedly for the same form, that
                    form should probably be assigned to the project in{' '}
                    <em>Inspection Settings</em>.
                  </p>
                  <div className='one-time-explainer__suppress'>
                    <label>
                      <input
                        type='checkbox'
                        checked={suppressOneTimeExplainer}
                        onChange={(e) =>
                          setSuppressOneTimeExplainer(e.target.checked)
                        }
                      />
                      Don&apos;t show this again
                    </label>
                  </div>
                </div>
              </Modal>

              {/* One Time Inspection Modal */}
              <Modal
                show={schedularModal}
                title='Schedule Inspection - One Time'
                handleClose={() => {
                  setSchedularModal(false);
                  setScheduleFormData({});
                }}
                handleSubmit={() => {
                  schedulerApi
                    .createProjectSchedule({
                      project_id: projectStore?.project?.id,
                      template_id: +scheduleFormData.schedule_template,
                      date_of_inspection: scheduleFormData.schedule_date,
                      inspector_id: scheduleFormData.schedule_inspector,
                      inspection_type: scheduleFormData.schedule_type,
                      notes: scheduleFormData.schedule_notes || '',
                    })
                    .then(() => {
                      setSchedularModal(false);
                      setScheduleFormData({});
                      // Trigger refresh of upcoming inspections section
                      setUpcomingRefreshToken((prev) => prev + 1);
                    })
                    .catch((error) => {
                      console.error('Error scheduling inspection:', error);
                      const axiosError = error as any;

                      if (
                        axiosError?.response?.status === 403 &&
                        axiosError?.response?.data?.error?.includes(
                          'Authorization & Agreement',
                        )
                      ) {
                        toast.error(
                          'The Authorization & Agreement must be signed by both parties before inspections can be started.',
                        );
                      } else {
                        toast.error(
                          'Failed to schedule inspection. Please try again.',
                        );
                      }
                    });
                }}
                submitBtnText='Schedule'
              >
                <section className='project-form'>
                  <section className='container'>
                    <FormSchemaFields
                      formData={scheduleFormData}
                      onChange={(field: string, value: any) => {
                        // Clear inspection type when template changes
                        if (field === 'schedule_template') {
                          setScheduleFormData((prev: any) => ({
                            ...prev,
                            [field]: value,
                            schedule_type: '', // Clear inspection type
                          }));
                        } else {
                          setScheduleFormData((prev: any) => ({
                            ...prev,
                            [field]: value,
                          }));
                        }
                      }}
                      schema={getSchedularFields({
                        inspectionTemplates: inspectionTemplates,
                        users: contacts.map((contact: any) => ({
                          id: contact.id,
                          name: `${contact.first_name} ${contact.last_name}`.trim(),
                        })),
                        primaryContactUsers: contacts.map((contact: any) => ({
                          id: contact.id,
                          name: `${contact.first_name} ${contact.last_name}`.trim(),
                        })),
                        inspectionTypesData: inspectionTypesData,
                        isLoadingInspectionTypes: isLoadingInspectionTypes,
                        selectedTemplate: scheduleFormData.schedule_template,
                        defaultInspectionTypeOptions:
                          defaultInspectionTypeOptions,
                      })}
                    />
                  </section>
                </section>
              </Modal>

              <div
                className={classnames('tab-pane-actions-primary', {
                  'right-aligned': !noInspections,
                })}
              >
                {permCheck('all', 'Certify') &&
                  projectStore.project?.status === 'active' &&
                  selectedInspections.length > 0 && (
                    <button
                      className='outline small certify-button'
                      onClick={() => setShowCertifyModal(true)}
                    >
                      Certify {selectedInspections.length} Inspection
                      {selectedInspections.length > 1 ? 's' : null}
                    </button>
                  )}

                {permCheck('all', 'Certify') &&
                !restrictedRoles.includes(role) &&
                projectStore.project?.status === 'active' &&
                inspectionsReadyForCompliance.length > 0 &&
                showSelectAllBtn &&
                projectStore.project &&
                projectStore.project.inspection_compliance_required === true ? (
                  <SelectAll
                    label={
                      hasSelectedAllInspections
                        ? `Deselect All Inspections`
                        : `Select All Inspections`
                    }
                    value={hasSelectedAllInspections}
                    onChange={() =>
                      setHasSelectedAllInspections(!hasSelectedAllInspections)
                    }
                  />
                ) : null}

                <div>
                </div>
              </div>
            </div>

            {/* Filter Inspection Modal */}
            <Modal
              show={filterModal}
              title='Filter Inspections'
              handleClose={() => {
                setFilterModal(false);
              }}
              handleSubmit={async () => {
                try {
                  // Set active filters which will trigger the useResource to refetch
                  const filters: any = {};

                  if (filterFormData.filter_template_type) {
                    filters.inspectionTemplateTypeId =
                      filterFormData.filter_template_type;
                  }

                  if (filterFormData.filter_inspection_type) {
                    filters.inspectionType =
                      filterFormData.filter_inspection_type;
                  }

                  setActiveFilters(filters);

                  // Refetch the main API with filters
                  await query.refetch();
                } catch (error) {
                  console.error('Error applying filters:', error);
                }
                setFilterModal(false);
              }}
              submitBtnText='Apply Filter'
            >
              <section className='project-form'>
                <section className='container'>
                  <FormSchemaFields
                    formData={filterFormData}
                    onChange={(field: string, value: any) => {
                      setFilterFormData((prev: any) => ({
                        ...prev,
                        [field]: value,
                      }));
                    }}
                    schema={{
                      filter_inspection_type: {
                        controlType: UIControlType.select,
                        label: 'Inspection Type',
                        placeholder: 'Select inspection type',
                        noOptionMessage: 'No Inspection Created',
                        options: inspectionTypeOptions.map((type) => ({
                          label: type,
                          value: type,
                        })),
                      },
                      filter_template_type: {
                        controlType: UIControlType.select,
                        label: 'Inspection Template Type',
                        placeholder: 'Select template type',
                        options: inspectionTemplateTypes.map(
                          (template: any) => ({
                            label: template.name,
                            value: template.id,
                          }),
                        ),
                      },
                    }}
                  />
                </section>
              </section>
            </Modal>

            {/* Schedule Button and Upcoming Inspections Section */}
            {!isPublicUser && (
              <>
                <div
                  className='schedule-inspection-header'
                  style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.5rem',
                    width: '100%',
                  }}
                >
                  {(filterFormData.filter_template_type ||
                    filterFormData.filter_inspection_type) && (
                    <Button
                      onClick={async () => {
                        setFilterFormData({});
                        setActiveFilters({});
                        await query.refetch();
                      }}
                      className='clear-filter-btn'
                      outline
                    >
                      Clear Filters
                    </Button>
                  )}
                  <Button
                    onClick={async () => {
                      // Fetch unique inspection template types when filter button is clicked
                      try {
                        const projectId = projectStore?.project?.id;

                        if (projectId) {
                          const [templateTypes, inspectionTypes] =
                            await Promise.all([
                              inspectionTemplateTypesApi.getUniqueByProject(
                                projectId,
                              ),
                              projectInspectionApi.getInspectionTypesByProject(
                                projectId,
                              ),
                            ]);

                          setInspectionTemplateTypes(templateTypes);
                          setInspectionTypeOptions(inspectionTypes);
                        }
                      } catch (error) {
                        console.error('Error fetching filter options:', error);
                      }
                      setFilterModal(true);
                    }}
                    className='filter-inspection-btn'
                    outline
                  >
                    Filter
                  </Button>
                  {projectStore.project?.status === 'active' &&
                    permCheck('create', 'Inspection') && (
                      <Button
                        onClick={() => setAssignedFormModal(true)}
                        className='schedule-inspection-btn primary'
                      >
                        Schedule Inspection
                      </Button>
                    )}
                </div>
                <UpcomingInspectionSection
                  className='upcoming-inspections-section'
                  refreshToken={upcomingRefreshToken}
                  onInspectionDeleted={() => query.refetch()}
                />
              </>
            )}

            {projectStore.project?.external_project ? (
              <ExternalProjectsInspectionListing
                inspections={filteredInspections}
                pagination={pagination}
                data={data}
                setPageval={setPageVal}
                pageVal={pageVal}
              />
            ) : (
              <InspectionListing
                inspections={filteredInspections}
                checkedInspections={selectedInspections}
                handleInspectionCheck={selectInspection}
                userCanSign={
                  permCheck('all', 'Certify') &&
                  !restrictedRoles.includes(role) &&
                  projectStore.project?.status === 'active'
                }
                isPublic={role === 'Public' || role === 'Public No Image'}
                setSelectedInspections={setSelectedInspections}
                setHasSelectedAllInspections={setHasSelectedAllInspections}
                pagination={pagination}
                data={data}
                setPageval={setPageVal}
                pageVal={pageVal}
              />
            )}

            {projectStore.inspections.length > 0 &&
            filteredInspections.length === 0 ? (
              <div className='empty-filter'>
                <p>No inspections available.</p>

                <ResetButton handleClick={() => setFilter('all')} />
              </div>
            ) : null}

            {showCertifyModal && (
              <BulkCertifyModal
                hideModal={() => setShowCertifyModal(false)}
                inspections={projectStore.inspections.filter(
                  (inspection: { id: number }) =>
                    selectedInspections.includes(inspection.id),
                )}
                handleSaveAnimation={handleSaveAnimation}
                onSubmit={() => query.refetch()}
              />
            )}
          </FormContextProvider>
          <SuccessNotification show={showSuccess} />
          {showAgreementModal && agreementState && (
            <AuthorizationAgreementModal
              show={showAgreementModal}
              onClose={() => setShowAgreementModal(false)}
              projectId={projectStore.project!.id}
              agreement={agreementState.agreement}
              signability={agreementState.signability}
              signerInformation={agreementState.signerInformation}
              bodyHtml={agreementState.agreementBodyHtml}
              onSigned={() => fetchAgreementState(projectStore.project!.id)}
            />
          )}
        </React.Fragment>
      ) : (
        <Loading what='Inspections' />
      )}
    </section>
  );
};

export default Inspections;
